import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { toast } from "react-toastify";
import { validateFilesComplete, ALLOWED_FILE_TYPES } from "./utils/fileHelpers";

const FileUploadField: React.FC = () => {
  const { setValue, watch, formState, trigger } = useFormContext();
  const [isDragging, setIsDragging] = useState(false);
  const files = watch("PiecesJustificatives") as File[] | undefined;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    
    // Filtrer par extension et type MIME
    const validFiles = droppedFiles.filter(file => {
      const hasValidExtension = ['.pdf', '.jpg', '.jpeg', '.png'].some(ext => 
        file.name.toLowerCase().endsWith(ext)
      );
      const hasValidMimeType = ALLOWED_FILE_TYPES.includes(file.type);
      return hasValidExtension && hasValidMimeType;
    });
    
    if (validFiles.length === 0 && droppedFiles.length > 0) {
      toast.error("Aucun fichier valide. Formats acceptés : PDF, JPEG, PNG");
      return;
    }
    
    // Limiter à 2 fichiers maximum
    const currentFiles = files || [];
    const remainingSlots = 2 - currentFiles.length;
    
    if (remainingSlots <= 0) {
      toast.error("Vous ne pouvez télécharger que 2 pièces justificatives maximum.");
      return;
    }
    
    const filesToAdd = validFiles.slice(0, remainingSlots);
    
    // Valider la taille et le type des fichiers (combiner anciens + nouveaux)
    const allFiles = [...currentFiles, ...filesToAdd];
    const piece1 = allFiles[0] || null;
    const piece2 = allFiles[1] || null;
    
    const validationError = validateFilesComplete(piece1, piece2);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    
    setValue("PiecesJustificatives", [...currentFiles, ...filesToAdd]);
    trigger("PiecesJustificatives");
    
    if (validFiles.length > remainingSlots) {
      toast.warning(`Seuls ${remainingSlots} fichier(s) ont été ajouté(s). Maximum 2 fichiers autorisés.`);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      
      // Filtrer par extension et type MIME
      const validFiles = selectedFiles.filter(file => {
        const hasValidExtension = ['.pdf', '.jpg', '.jpeg', '.png'].some(ext => 
          file.name.toLowerCase().endsWith(ext)
        );
        const hasValidMimeType = ALLOWED_FILE_TYPES.includes(file.type);
        return hasValidExtension && hasValidMimeType;
      });
      
      if (validFiles.length === 0 && selectedFiles.length > 0) {
        toast.error("Aucun fichier valide. Formats acceptés : PDF, JPEG, PNG");
        e.target.value = "";
        return;
      }
      
      // Limiter à 2 fichiers maximum
      const currentFiles = files || [];
      const remainingSlots = 2 - currentFiles.length;
      
      if (remainingSlots <= 0) {
        toast.error("Vous ne pouvez télécharger que 2 pièces justificatives maximum.");
        e.target.value = ""; // Réinitialiser l'input
        return;
      }
      
      const filesToAdd = validFiles.slice(0, remainingSlots);
      
      // Valider la taille et le type des fichiers (combiner anciens + nouveaux)
      const allFiles = [...currentFiles, ...filesToAdd];
      const piece1 = allFiles[0] || null;
      const piece2 = allFiles[1] || null;
      
      const validationError = validateFilesComplete(piece1, piece2);
      if (validationError) {
        toast.error(validationError);
        e.target.value = "";
        return;
      }
      
      setValue("PiecesJustificatives", [...currentFiles, ...filesToAdd]);
      trigger("PiecesJustificatives");
      
      if (validFiles.length > remainingSlots) {
        toast.warning(`Seuls ${remainingSlots} fichier(s) ont été ajouté(s). Maximum 2 fichiers autorisés.`);
      }
      
      e.target.value = ""; // Réinitialiser l'input pour permettre de re-sélectionner
    }
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = [...(files || [])];
    updatedFiles.splice(index, 1);
    setValue("PiecesJustificatives", updatedFiles);
    trigger("PiecesJustificatives");
  };

  const handleViewFile = (file: File) => {
    if (file.type.startsWith('image/')) {
      // Pour les images, lire le fichier et l'afficher
      const reader = new FileReader();
      reader.onload = (e) => {
        const newWindow = window.open('', '_blank');
        if (newWindow && e.target?.result) {
          newWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>${file.name}</title>
                <style>
                  body {
                    margin: 0;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 100vh;
                    background-color: #2d2d2d;
                  }
                  img {
                    max-width: 95%;
                    max-height: 95vh;
                    object-fit: contain;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.3);
                  }
                </style>
              </head>
              <body>
                <img src="${e.target.result}" alt="${file.name}" />
              </body>
            </html>
          `);
          newWindow.document.close();
        }
      };
      reader.readAsDataURL(file);
    } else {
      // Pour les PDFs et autres documents
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    }
  };

  return (
    <div className="mb-3">
      <label className="d-block mb-2">
        Téléchargement de vos pièces justificatives <span className="text-danger">*</span>
      </label>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          border: isDragging ? "2px solid #0d6efd" : "2px dashed #dee2e6",
          borderRadius: "4px",
          padding: "2rem",
          textAlign: "center",
          backgroundColor: isDragging ? "#f0f8ff" : "#fff",
          cursor: "pointer",
        }}
      >
        <div className="mb-2">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6c757d"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ margin: "0 auto", display: "block" }}
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </div>
        <p className="mb-1">Faites glisser et déposez le fichier ici</p>
        <p className="mb-2">Ou</p>
        <label htmlFor="file-upload" className="btn btn-secondary" style={{ cursor: "pointer" }}>
          Parcourir
        </label>
        <input
          id="file-upload"
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />
      </div>

      {files && files.length > 0 && (
        <div className="mt-3">
          {files.map((file, index) => (
            <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded">
              <div>{file.name}</div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleViewFile(file)}
                >
                  Voir
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleRemoveFile(index)}
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-2" style={{ fontSize: "0.9em", color: "#6c757d" }}>
        <p className="mb-1">
          <strong>Maximum 2 pièces justificatives.</strong> Formats acceptés : PDF, JPEG, PNG
        </p>
        <p className="mb-1">
          <strong>Taille maximale totale :</strong> 7 MB
        </p>
        <p className="mb-1">Associations : statut ou annonce au JO et extrait RNA</p>
        <p className="mb-0">Sociétés : extrait de Kbis ou extrait du registre du commerce pour les sociétés étrangères</p>
      </div>

      {formState.errors.PiecesJustificatives && (
        <div className="text-danger mt-1">
          {formState.errors.PiecesJustificatives.message as string}
        </div>
      )}
    </div>
  );
};

export default FileUploadField;
